import { useState } from 'react';
import { Calendar, CalendarPlus, X } from 'lucide-react';
import { buildBookingIcsUrl, buildGoogleCalendarUrl, buildSingleEventIcs, downloadIcs } from '../../utils/calendarIcs';

const isIos = typeof navigator !== 'undefined' && /iPhone|iPad|iPod/i.test(navigator.userAgent);

type Props = {
  uid: string;
  serviceName: string;
  slotStart: string;
  slotEnd: string;
  filename: string;
  label: string;
  labelGoogle?: string;
  labelOther?: string;
  bookingId?: string;
  tenantId?: string;
};

export function CalendarPickerButton({
  uid,
  serviceName,
  slotStart,
  slotEnd,
  filename,
  label,
  labelGoogle = 'Google Calendar',
  labelOther = 'Other calendar',
  bookingId,
  tenantId,
}: Props) {
  const [open, setOpen] = useState(false);

  const googleUrl = buildGoogleCalendarUrl({ serviceName, slotStart, slotEnd });

  // Render as <a href="webcal://..."> on iOS so the user's tap is treated as a
  // link navigation — confirmed to open Calendar.app from WhatsApp at feb31dfa.
  const webcalUrl =
    isIos && bookingId && tenantId
      ? buildBookingIcsUrl(bookingId, tenantId)?.replace(/^https?:\/\//, 'webcal://')
      : undefined;

  const handleOther = () => {
    setOpen(false);
    const ics = buildSingleEventIcs({ uid, serviceName, slotStart, slotEnd });
    const httpsUrl = bookingId && tenantId ? buildBookingIcsUrl(bookingId, tenantId) : undefined;
    downloadIcs(ics, filename, httpsUrl);
  };

  if (!open) {
    return (
      <button
        type="button"
        className="close-button"
        style={{ width: '100%' }}
        onClick={() => setOpen(true)}
      >
        <CalendarPlus size={16} strokeWidth={1.9} aria-hidden style={{ flexShrink: 0 }} />
        {label}
      </button>
    );
  }

  return (
    <div className="calendar-picker">
      <a
        href={googleUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="close-button calendar-picker-btn"
        onClick={() => setOpen(false)}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden style={{ flexShrink: 0 }}>
          <path d="M21.805 10.023H21V10H12v4h5.651C16.896 16.07 14.649 17.5 12 17.5c-3.038 0-5.5-2.462-5.5-5.5S8.962 6.5 12 6.5c1.396 0 2.67.523 3.633 1.377l2.828-2.828C16.83 3.44 14.55 2.5 12 2.5 6.753 2.5 2.5 6.753 2.5 12S6.753 21.5 12 21.5c5.247 0 9.5-4.253 9.5-9.5 0-.637-.065-1.259-.195-1.977z" fill="#4285F4"/>
          <path d="M3.153 7.345l3.286 2.411C7.34 7.67 9.517 6.5 12 6.5c1.396 0 2.67.523 3.633 1.377l2.828-2.828C16.83 3.44 14.55 2.5 12 2.5c-3.8 0-7.09 2.1-8.847 5.845z" fill="#EA4335"/>
          <path d="M12 21.5c2.496 0 4.75-.955 6.453-2.51l-2.98-2.52A5.46 5.46 0 0 1 12 17.5c-2.637 0-4.876-1.418-5.643-3.47L3.08 16.49C4.816 20.218 8.09 21.5 12 21.5z" fill="#34A853"/>
          <path d="M21.805 10.023H21V10H12v4h5.651a5.515 5.515 0 0 1-1.878 2.47l2.979 2.52C18.688 19.37 21.5 17 21.5 12c0-.637-.065-1.259-.195-1.977z" fill="#FBBC05"/>
        </svg>
        {labelGoogle}
      </a>
      {webcalUrl ? (
        <a
          href={webcalUrl}
          className="close-button calendar-picker-btn calendar-picker-btn--secondary"
          onClick={() => setOpen(false)}
        >
          <Calendar size={16} strokeWidth={1.9} aria-hidden style={{ flexShrink: 0 }} />
          {labelOther}
        </a>
      ) : (
        <button
          type="button"
          className="close-button calendar-picker-btn calendar-picker-btn--secondary"
          onClick={handleOther}
        >
          <Calendar size={16} strokeWidth={1.9} aria-hidden style={{ flexShrink: 0 }} />
          {labelOther}
        </button>
      )}
      <button
        type="button"
        className="calendar-picker-cancel"
        onClick={() => setOpen(false)}
      >
        <X size={16} strokeWidth={2} aria-hidden />
      </button>
    </div>
  );
}
